import React, { useState } from 'react';
import { Trophy, Calendar, TrendingUp, Clock, Users, Activity, Database, Zap } from 'lucide-react';
import { useLeaderboard } from '../hooks/useLeaderboard';

interface DataStatus {
  mode: string;
  coverage?: {
    total_tweets: number;
    earliest_date: string;
    latest_date: string;
    active_days: number;
  };
  last_collection?: {
    type: string;
    status: string;
    count: number;
    timestamp: string;
  };
  has_sufficient_historical: boolean;
}

interface LeaderboardData {
  data_status?: DataStatus;
  overall_stats?: {
    unique_users: number;
    total_tweets: number;
    total_engagement: number;
    avg_engagement: number;
  };
  leaderboard?: Array<{
    rank: number;
    author_username: string;
    author_name: string;
    author_profile_image: string;
    author_verified: boolean;
    tweet_count: number;
    total_likes: number;
    total_retweets: number;
    total_replies: number;
    total_engagement: number;
    avg_engagement: number;
    consistency_score: number;
  }>;
  trending_hashtags?: Array<{
    hashtag: string;
    total_count: number;
    total_engagement: number;
  }>;
  date_range?: {
    from: string;
    to: string;
  };
}

export default function Leaderboard() {
  const [timeframe, setTimeframe] = useState('week');
  const { data, isLoading, error } = useLeaderboard(timeframe) as { 
    data: LeaderboardData | undefined; 
    isLoading: boolean; 
    error: string | null; 
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-900/20 border border-red-500 rounded-lg p-6">
            <h2 className="text-xl font-bold text-red-400 mb-2">Error Loading Leaderboard</h2>
            <p className="text-red-300">{error || 'Failed to load data'}</p>
          </div>
        </div>
      </div>
    );
  }

  const timeframeOptions = [
    { value: 'day', label: 'Today', icon: Clock },
    { value: 'week', label: 'This Week', icon: Calendar },
    { value: 'month', label: 'This Month', icon: TrendingUp },
    { value: 'all', label: 'All Time', icon: Database }
  ];

  const getDataStatusColor = (mode) => {
    switch (mode) {
      case 'real_time': return 'text-green-400 bg-green-900/20 border-green-500';
      case 'historical': return 'text-yellow-400 bg-yellow-900/20 border-yellow-500';
      default: return 'text-gray-400 bg-gray-900/20 border-gray-500';
    }
  };

  const getDataStatusIcon = (mode: string) => {
    switch (mode) {
      case 'real_time': return <Zap className="h-4 w-4" />;
      case 'historical': return <Database className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const formatDataStatus = (status) => {
    if (!status) return 'Unknown';
    
    switch (status.mode) {
      case 'real_time':
        return 'Live Data Collection';
      case 'historical':
        return `Collecting Historical Data (${status.coverage?.total_tweets || 0} tweets)`;
      default:
        return 'Data Collection Status Unknown';
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Trophy className="h-8 w-8 text-yellow-400" />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              Community Leaderboard
            </h1>
          </div>
          
          {/* Data Status Banner */}
          {data.data_status && (
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm ${getDataStatusColor(data.data_status.mode)}`}>
              {getDataStatusIcon(data.data_status.mode)}
              <span>{formatDataStatus(data.data_status)}</span>
              {data.data_status.coverage?.earliest_date && (
                <span className="text-xs opacity-75">
                  • From {new Date(data.data_status.coverage.earliest_date).toLocaleDateString()}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Timeframe Selector */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3">
            {timeframeOptions.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => setTimeframe(value)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  timeframe === value
                    ? 'bg-yellow-500 text-black font-semibold'
                    : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Overall Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="h-6 w-6 text-blue-400" />
              <span className="text-gray-400">Total Users</span>
            </div>
            <span className="text-2xl font-bold text-white">
              {data.overall_stats?.unique_users || 0}
            </span>
          </div>
          
          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="h-6 w-6 text-green-400" />
              <span className="text-gray-400">Total Tweets</span>
            </div>
            <span className="text-2xl font-bold text-white">
              {data.overall_stats?.total_tweets || 0}
            </span>
          </div>
          
          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <Activity className="h-6 w-6 text-purple-400" />
              <span className="text-gray-400">Total Engagement</span>
            </div>
            <span className="text-2xl font-bold text-white">
              {(data.overall_stats?.total_engagement || 0).toLocaleString()}
            </span>
          </div>
          
          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="h-6 w-6 text-yellow-400" />
              <span className="text-gray-400">Active Days</span>
            </div>
            <span className="text-2xl font-bold text-white">
              {data.data_status?.coverage?.active_days || 0}
            </span>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="bg-gray-900/50 border border-gray-700 rounded-lg overflow-hidden">
          <div className="p-6 border-b border-gray-700">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-400" />
              Top Contributors {timeframe === 'all' ? '(All Time)' : `(${timeframeOptions.find(t => t.value === timeframe)?.label})`}
            </h2>
            <p className="text-gray-400 mt-1">
              Ranked by total engagement (likes + retweets + replies)
              {data.date_range && (
                <span className="ml-2 text-xs">
                  • {new Date(data.date_range.from).toLocaleDateString()} to {new Date(data.date_range.to).toLocaleDateString()}
                </span>
              )}
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Rank</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">User</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Tweets</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Likes</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Retweets</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Replies</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Total Engagement</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase">Consistency</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {data.leaderboard?.map((user) => (
                  <tr key={user.author_username} className="hover:bg-gray-800/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {user.rank <= 3 && (
                          <Trophy className={`h-5 w-5 mr-2 ${
                            user.rank === 1 ? 'text-yellow-400' :
                            user.rank === 2 ? 'text-gray-300' : 'text-orange-400'
                          }`} />
                        )}
                        <span className="font-semibold text-lg">{user.rank}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={user.author_profile_image || '/placeholder-avatar.png'}
                          alt={user.author_name}
                          className="h-10 w-10 rounded-full bg-gray-700"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/placeholder-avatar.png';
                          }}
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">{user.author_name}</span>
                            {user.author_verified && (
                              <span className="text-blue-400 text-xs">✓</span>
                            )}
                          </div>
                          <span className="text-sm text-gray-400">@{user.author_username}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-white font-medium">{user.tweet_count}</td>
                    <td className="px-6 py-4 text-red-400 font-medium">{user.total_likes.toLocaleString()}</td>
                    <td className="px-6 py-4 text-green-400 font-medium">{user.total_retweets.toLocaleString()}</td>
                    <td className="px-6 py-4 text-blue-400 font-medium">{user.total_replies.toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-yellow-400 font-bold text-lg">
                          {user.total_engagement.toLocaleString()}
                        </span>
                        <span className="text-xs text-gray-400">
                          Avg: {user.avg_engagement.toLocaleString()}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="bg-gray-700 rounded-full h-2 w-16">
                          <div
                            className={`h-2 rounded-full ${
                              user.consistency_score >= 80 ? 'bg-green-400' :
                              user.consistency_score >= 60 ? 'bg-yellow-400' :
                              user.consistency_score >= 40 ? 'bg-orange-400' : 'bg-red-400'
                            }`}
                            style={{ width: `${user.consistency_score}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400">{user.consistency_score}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Trending Hashtags */}
        {data.trending_hashtags?.length > 0 && (
          <div className="mt-8 bg-gray-900/50 border border-gray-700 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-400" />
              Trending Hashtags
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {data.trending_hashtags.map((hashtag, index) => (
                <div key={hashtag.hashtag} className="bg-gray-800 rounded-lg p-4">
                  <div className="text-blue-400 font-medium">{hashtag.hashtag}</div>
                  <div className="text-sm text-gray-400 mt-1">
                    {hashtag.total_count} tweets
                  </div>
                  <div className="text-xs text-gray-500">
                    {hashtag.total_engagement.toLocaleString()} engagement
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 